import React from 'react';
import { Target, ArrowRight, Clock, Users, Layers } from 'lucide-react';
import type { Scenario } from '../../types/prioritization-selector';

interface ScenarioSelectorProps {
  scenarios: Scenario[];
  onSelect: (scenarioId: string) => void;
  onStartOver: () => void;
}

const ScenarioSelector: React.FC<ScenarioSelectorProps> = ({
  scenarios,
  onSelect,
  onStartOver
}) => {
  const getScenarioIcon = (scenarioId: string) => {
    switch (scenarioId) {
      case 'task':
        return <Target className="w-8 h-8 text-[var(--text-ter)]" />;
      case 'initiative':
        return <Layers className="w-8 h-8 text-[var(--green)]" />;
      case 'portfolio':
        return <Users className="w-8 h-8 text-[var(--text-ter)]" />;
      default:
        return <Clock className="w-8 h-8 text-[var(--text-sec)]" />;
    }
  };

  const getScenarioColor = (scenarioId: string) => {
    switch (scenarioId) {
      case 'task':
        return 'border-[var(--slate)] hover:border-[var(--slate)] hover:shadow-blue-100';
      case 'initiative':
        return 'border-[var(--green)] hover:border-[var(--green)] hover:shadow-green-100';
      case 'portfolio':
        return 'border-[var(--slate)] hover:border-[var(--slate)] hover:shadow-purple-100';
      default:
        return 'border-[var(--rule)] hover:border-[var(--rule)]';
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-display font-bold text-[var(--text-pri)] mb-4">
          Find Your Perfect Prioritization Approach
        </h1>
        <p className="text-xl text-[var(--text-sec)] mb-8 max-w-2xl mx-auto">
          Our decision tree will guide you to the right tool bundle for your specific situation
        </p>
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-12">
        {scenarios.map((scenario) => (
          <button
            key={scenario.id}
            onClick={() => onSelect(scenario.id)}
            className={`p-8 bg-[var(--bg-raised)]  shadow-sm border-2 hover:shadow-lg transition-all text-left group ${getScenarioColor(scenario.id)}`}
          >
            <div className="flex items-center mb-4">
              {getScenarioIcon(scenario.id)}
              <h3 className="text-2xl font-heading font-semibold text-[var(--text-pri)] ml-4">
                {scenario.name}
              </h3>
            </div>
            <p className="text-[var(--text-sec)] mb-4 text-lg">
              {scenario.description}
            </p>
            <div className="text-sm text-[var(--text-sec)] mb-4">
              <strong>Context:</strong> {scenario.context}
            </div>
            <div className="flex items-center text-[var(--text-ter)] font-medium group-hover:text-[var(--text-ter)]">
              <span>Start Decision Tree</span>
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </div>
          </button>
        ))}
      </div>

      <div className="text-center">
        <div className="bg-[var(--surface)] p-6 mb-8">
          <h3 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
            How It Works
          </h3>
          <p className="text-[var(--text-sec)]">
            Answer 3-6 simple questions and we'll recommend the perfect tool bundle 
            for your prioritization needs. Each bundle includes specific methods, 
            processes, and tools tailored to your situation.
          </p>
        </div>

        <button
          onClick={onStartOver}
          className="text-[var(--text-sec)] hover:text-[var(--text-pri)] font-medium"
        >
          Or start with a comprehensive analysis
        </button>
      </div>
    </div>
  );
};

export default ScenarioSelector;

